// src/components/table/ConsumptionTable.tsx

import {
  KeyboardDoubleArrowDownTwoTone,
  KeyboardDoubleArrowUpTwoTone,
  SettingsTwoTone,
} from '@mui/icons-material'
import {
  Collapse,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material'
import { useState, useEffect } from 'react'

import { useRoomsMode } from '@/hooks'
import { FolderId } from '@/types/domain'

import { HStack, SheetSelector } from '..'
import {
  germanDecimals,
  MMMMYYYY,
  removeUnits,
  sensorUnit,
  userLocales,
  WDDDMMYY,
  WDDDMMYYHHMM,
  YYYY,
} from '../../api'

const useAbbreviation = (input: string): string =>
  input.replace('Mitarbeiter', 'MA').replace('Zimmer', 'Zi.')

// 1000000 -> 1.000.000
const digitGrouping = (digit: number): string =>
  Number(digit).toLocaleString(userLocales, germanDecimals)

type ConsumptionTableProps = {
  id: FolderId
  colors: Record<string, string>
  cols: { [key: string]: boolean }
  data: { [key: string]: any[][] }
  granularity: string
  medium: string
  unitConfig: { header: boolean; values: boolean }
  onClick?: () => void
}

export const ConsumptionTable = ({
  id,
  colors,
  cols,
  data,
  granularity,
  medium,
  unitConfig,
  onClick,
  ...rest
}: ConsumptionTableProps) => {
  const roomsMode: string = useRoomsMode(id)

  const { header: uch, values: ucv } = unitConfig

  const [selectedSheet, selectSheet] = useState(0)
  const [open, setOpen] = useState(true)

  // calc meter unit
  //const meterUnit = Object.values(data)[selectedSheet]?.[0][9].split(' ')[1]

  const headerData = [
    { align: 'left', label: 'Datum', accessor: 'date' },
    {
      align: 'center',
      label: `Verbrauch ${uch === true ? ' (' + sensorUnit(medium) + ')' : ''}`,
      accessor: 'actual_consumption',
    },
    {
      align: 'center',
      label: `Trend ${uch === true ? '(%)' : ''}`,
      accessor: 'trend',
    },
    {
      align: 'center',
      label: 'Zählerstand',
      accessor: 'counter_reading',
    },
    {
      align: 'center',
      label: `CO₂-Emissionen ${uch === true ? '(kg)' : ''}`,
      accessor: 'emissions',
    },
    {
      align: 'center',
      label: `Verbrauch/m² ${uch === true ? '(' + sensorUnit(medium) + ')' : ''}`,
      accessor: 'area_consumption',
    },
    {
      align: 'center',
      label: `Verbrauch/${useAbbreviation(roomsMode)} ${uch === true ? '(' + sensorUnit(medium) + ')' : ''}`,
      accessor: 'employee_consumption',
    },
    {
      align: 'center',
      label: `Kosten${uch === true ? ' (€)' : ''}`,
      accessor: 'actual_costs',
    },
    {
      align: 'center',
      label: `Kosten/m²${uch === true ? ' (€)' : ''}`,
      accessor: 'area_costs',
    },
    {
      align: 'center',
      label: `Kosten/${useAbbreviation(roomsMode)}${uch === true ? ' (€)' : ''}`,
      accessor: 'employee_costs',
    },
    {
      align: 'center',
      label: `Benchmark${uch === true ? ' (%)' : ''}`,
      accessor: 'benchmark',
    },
  ]

  const sheetNames = Object.keys(data)

  useEffect(() => {
    if (selectedSheet !== 0 && selectedSheet + 1 > sheetNames.length)
      selectSheet(sheetNames.length - 1)
  }, [sheetNames, selectedSheet])

  return (
    <HStack>
      {sheetNames.map((sheetName, key) =>
        key === selectedSheet ? (
          <Paper
            key={sheetName}
            elevation={0}
            sx={{ border: 'solid 1px #DDDDDD' }}
          >
            <Toolbar sx={{ backgroundColor: '#EEE' }}>
              <HStack
                alignItems={'center'}
                justifyContent={'space-between'}
                sx={{ width: 1 }}
              >
                <HStack alignItems={'center'}>
                  <IconButton onClick={() => setOpen(prev => !prev)}>
                    {open ? (
                      <KeyboardDoubleArrowDownTwoTone />
                    ) : (
                      <KeyboardDoubleArrowUpTwoTone />
                    )}
                  </IconButton>

                  <SheetSelector
                    colors={colors}
                    onChange={sheet => selectSheet(sheet)}
                    data={sheetNames}
                    title={sheetName}
                  />

                  <Typography sx={{ fontSize: 18, fontWeight: 700, ml: 2 }}>
                    {sheetName}
                  </Typography>

                  <Typography sx={{ fontSize: 18, fontWeight: 700, ml: 1 }}>
                    {granularity}
                  </Typography>
                </HStack>

                <IconButton onClick={onClick} {...rest}>
                  <SettingsTwoTone />
                </IconButton>
              </HStack>
            </Toolbar>

            <Divider />

            <Collapse in={open}>
              <TableContainer sx={{ maxHeight: 'calc(80vh)' }}>
                <Table sx={{ maxWidth: 1, tableLayout: 'fixed' }}>
                  <TableHead
                    sx={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: theme => theme.palette.background.paper,
                      zIndex: 2,
                    }}
                  >
                    <TableRow>
                      {headerData.map(cell =>
                        cols[cell.accessor] ? (
                          <TableCell
                            align={
                              cell.align as
                                | 'left'
                                | 'center'
                                | 'right'
                                | 'inherit'
                                | 'justify'
                            }
                            key={cell.accessor}
                          >
                            {cell.label}
                          </TableCell>
                        ) : null
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data[sheetName].map(row => (
                      <TableRow key={row[0]}>
                        {[
                          {
                            accessor: 'date',
                            align: 'left',
                            sx: null,
                            value:
                              typeof row[0] === 'string'
                                ? row[0]
                                : new Date(row[0]).toLocaleString(
                                    userLocales,
                                    granularity === 'Stündlich'
                                      ? WDDDMMYYHHMM
                                      : granularity === 'Täglich'
                                        ? WDDDMMYY
                                        : granularity === 'Monatlich'
                                          ? MMMMYYYY
                                          : granularity === 'Jährlich'
                                            ? YYYY
                                            : WDDDMMYY
                                  ),
                          },
                          {
                            accessor: 'actual_consumption',
                            align: 'center',
                            sx: null,
                            value:
                              typeof row[1] !== 'string'
                                ? `${digitGrouping(
                                    row[1].toFixed(2)
                                  )} ${ucv === false ? '' : sensorUnit(medium)}`
                                : `${ucv === false ? removeUnits(row[1]) : row[1]}`,
                          },
                          {
                            accessor: 'trend',
                            align: 'center',
                            sx: {
                              color: row[2]?.includes('▲')
                                ? 'red'
                                : row[2]?.includes('▼')
                                  ? 'green'
                                  : 'black',
                            },
                            value: `${row[2]}${ucv === true && row[2] !== '-' ? ' %' : ''}`,
                          },
                          {
                            accessor: 'counter_reading',
                            align: 'center',
                            sx: null,
                            value: ucv === false ? removeUnits(row[9]) : row[9],
                          },
                          {
                            accessor: 'emissions',
                            align: 'center',
                            sx: null,
                            value: ucv === false ? removeUnits(row[3]) : row[3],
                          },
                          {
                            accessor: 'area_consumption',
                            align: 'center',
                            sx: null,
                            value: ucv === false ? removeUnits(row[4]) : row[4],
                          },
                          {
                            accessor: 'employee_consumption',
                            align: 'center',
                            sx: null,
                            value: ucv === false ? removeUnits(row[5]) : row[5],
                          },
                          {
                            accessor: 'actual_costs',
                            align: 'center',
                            sx: null,
                            value: ucv === false ? removeUnits(row[6]) : row[6],
                          },
                          {
                            accessor: 'area_costs',
                            align: 'center',
                            sx: null,
                            value: ucv === false ? removeUnits(row[7]) : row[7],
                          },
                          {
                            accessor: 'employee_costs',
                            align: 'center',
                            sx: null,
                            value:
                              ucv === false ? removeUnits(row[10]) : row[10],
                          },
                          {
                            accessor: 'benchmark',
                            align: 'center',
                            sx: {
                              color: row[8]?.includes('▲')
                                ? 'red'
                                : row[8]?.includes('▼')
                                  ? 'green'
                                  : 'black',
                            },
                            value: `${row[8]}${ucv === true && row[8] !== '-' ? ' %' : ''}`,
                          },
                        ].map(cell =>
                          cols[cell.accessor] ? (
                            <TableCell
                              align={
                                cell.align as
                                  | 'left'
                                  | 'center'
                                  | 'right'
                                  | 'inherit'
                                  | 'justify'
                              }
                              key={cell.accessor}
                              sx={cell.sx}
                            >
                              {cell.value}
                            </TableCell>
                          ) : null
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        ) : null
      )}
    </HStack>
  )
}
